// The kanta/salkku overview's situation cards — same Tier-2 chrome as the generic
// SituationCards (fe.module-card + CardGlowOverlay), content swapped for the lens-agnostic
// `OverviewBucket[]` every lens fills with its own questions
// (docs/spec/concepts.md §2.4 → "Portfolio-scope overview grammar"). Generic SituationCards
// is not reused here — its content is owner energy/€ and static role mocks, which no overview
// lens (setup or judgment) may show.
//
// A non-zero card is a facet selector, not a breakdown popover: clicking it selects it (accent
// hue marks selection — tone stays semantic, never accent, per frontend/CLAUDE.md), clicking the
// selected card again clears it. A zero-count card stays a static, non-interactive tile exactly
// as before.
import { Filter } from "lucide-react";

import { moduleCard } from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";
import { KpiStatusTileContent } from "@/components/ui/kpi-status-tile-content";
import { cn } from "@/lib/utils";
import type { OverviewBucket, OverviewCopy } from "@/lib/overview-lens";

type SituationCardCopy = Pick<
  OverviewCopy,
  "situationCardSubtitle" | "situationCardAllDoneLabel" | "emptyScopeHint"
>;

export function OverviewSituationCards({
  buckets,
  totalCount,
  loading,
  copy,
  selectedKey,
  onSelect,
}: {
  buckets: OverviewBucket[];
  /** Size of the scoped building set — distinguishes "nothing in scope" from "scope is set up". */
  totalCount: number;
  /** True until the first per-building batch has settled — after that, counts update live as
   *  more buildings settle rather than blocking on the whole scoped set. */
  loading: boolean;
  copy: SituationCardCopy;
  /** The bucket key currently narrowing the tile grid/map, or `null` when no facet is active. */
  selectedKey: string | null;
  /** Toggles a bucket as the active facet — the caller clears it again on re-selection. */
  onSelect: (key: string) => void;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={cn(moduleCard, "h-28 animate-pulse p-4")} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {buckets.map((bucket) => (
        <SituationCard
          key={bucket.key}
          bucket={bucket}
          totalCount={totalCount}
          copy={copy}
          selected={selectedKey === bucket.key}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function SituationCard({
  bucket,
  totalCount,
  copy,
  selected,
  onSelect,
}: {
  bucket: OverviewBucket;
  totalCount: number;
  copy: SituationCardCopy;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  const Icon = bucket.icon;
  const empty = totalCount === 0;
  const tone = empty ? "empty" : bucket.count === 0 ? "ok" : "warn";
  const value = empty ? "—" : String(bucket.count);
  const sub = empty
    ? copy.emptyScopeHint
    : bucket.count === 0
      ? copy.situationCardAllDoneLabel
      : copy.situationCardSubtitle(bucket.count);
  const interactive = bucket.count > 0;

  const cardInner = (
    <>
      <KpiStatusTileContent
        icon={Icon}
        label={bucket.label}
        value={value}
        tone={tone}
        hint={sub}
        variant="situation"
        active={selected}
        accent="accent"
      />
      {interactive && (
        <div
          className={cn(
            "relative mt-2 pt-2 border-t border-border/60 text-[10px] uppercase tracking-[0.16em] flex items-center gap-1 transition-colors",
            selected ? "text-accent-foreground" : "text-primary/80",
          )}
        >
          <Filter className="h-3 w-3" />
          {selected ? "Rajaus käytössä" : "Rajaa kiinteistöt"}
        </div>
      )}
    </>
  );

  if (!interactive) {
    return (
      <div className={cn(moduleCard, "p-4")}>
        <CardGlowOverlay />
        {cardInner}
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(bucket.key)}
      className={cn(
        moduleCard,
        "p-4 text-left cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50",
        selected && "border-accent shadow-[0_0_0_1px_var(--accent)]",
      )}
    >
      <CardGlowOverlay />
      {cardInner}
    </button>
  );
}
